import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { useUsage } from "../hooks/useUsage";
import { api } from "../utils/apiClient";
import UsageMeter from "./UsageMeter";

const FEATURE_ORDER = ["audits", "prompt_tests", "persona_reports", "ota_queries"];

const FEATURE_LABELS = {
  audits: "AI Audits",
  prompt_tests: "AI reputation reports",
  persona_reports: "Persona Visibility Reports",
  ota_queries: "OTA vs Direct Queries",
};

// Display names for add-ons. Mirrors the backend's ADDONS_CONTENT — the /payment/addon-status
// endpoint returns keys/status/dates, not display copy (same pattern as Checkout.jsx's ADDON_NAMES).
const ADDON_LABELS = {
  white_glove_onboarding: "White-glove onboarding",
  ai_optimized_website_build: "AI-optimized website build",
  schema_markup_implementation: "Schema markup implementation",
  extra_property: "Extra property",
  done_for_you_strategy: "Done-for-you strategy",
  strategy_session: "Strategy session",
};

const ADDON_PURCHASED_STATUSES = new Set(["paid", "active"]);

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

const formatPrice = (cents) => {
  if (cents === null || cents === undefined) return null;
  const dollars = cents / 100;
  const formatted = Number.isInteger(dollars)
    ? dollars.toLocaleString("en-US")
    : dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `$${formatted}`;
};

const formatDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

// One line of metadata under an add-on: when it was bought, and — for monthly add-ons — when it
// renews (one-time add-ons don't renew).
const addonTimingLabel = (addon) => {
  const parts = [];
  const purchased = formatDate(addon.purchased_at);
  if (purchased) parts.push(`Purchased ${purchased}`);
  if (addon.cadence === "monthly") {
    const renews = formatDate(addon.current_period_end);
    if (renews) parts.push(`Renews ${renews}`);
  } else {
    parts.push("One-time");
  }
  return parts.join(" · ");
};

const Subscription = () => {
  const { user } = useAuth();
  const { byFeature: usage, loading } = useUsage();

  const [addons, setAddons] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);

  const isPaid = Boolean(user?.subscriptionPlan && user.subscriptionPlan !== "free");

  // Paid plan changes go through Stripe's Billing Portal (upgrade/downgrade with proration).
  // return_path brings the user back here; ?billing=updated (added server-side) makes
  // AuthContext refresh the session so the new plan shows without a re-login.
  const handleChangePlan = async () => {
    setPortalError(null);
    setPortalLoading(true);
    try {
      const res = await api.post("/payment/portal", { return_path: "/subscription" });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setPortalError("Couldn't open the billing portal. Please try again.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err.message || "Couldn't open the billing portal.";
      setPortalError(detail);
    } finally {
      setPortalLoading(false);
    }
  };

  // Only logged-in users can have add-ons; when there's no user the component renders the
  // "log in" view below, so we can bail without touching state. All setState happens inside
  // the async callbacks (never synchronously in the effect body).
  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    api
      .get("/payment/addon-status")
      .then((res) => {
        if (!active) return;
        const purchased = (res.data?.addons || []).filter((addon) =>
          ADDON_PURCHASED_STATUSES.has(addon.status),
        );
        setAddons(purchased);
      })
      .catch(() => {
        // Non-blocking — the add-ons section just stays empty if this fails.
        if (active) setAddons([]);
      })
      .finally(() => {
        if (active) setAddonsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user]);

  if (!user) {
    return (
      <section className="pt-12 pb-24 px-8 max-w-screen-md mx-auto text-center">
        <p className="text-on-surface-variant">Log in to view your subscription details.</p>
      </section>
    );
  }

  return (
    <section className="pt-12 pb-24 px-8 max-w-screen-md mx-auto">
      <div className="mb-10">
        <h1 className="text-3xl font-bold text-on-surface tracking-tight">Subscription</h1>
        <p className="text-on-surface-variant mt-2">Your plan, add-ons, and monthly usage.</p>
      </div>

      <div className="rounded-2xl bg-surface-container-lowest shadow-cloud ring-1 ring-outline-variant/20 p-6 mb-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider">
              Current Plan
            </div>
            <div className="text-xl font-bold text-on-surface mt-1">
              {capitalize(user.subscriptionPlan)}
            </div>
          </div>
          {isPaid ? (
            <button
              type="button"
              onClick={handleChangePlan}
              disabled={portalLoading}
              className="px-4 py-2 rounded-xl bg-primary text-on-primary text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-default"
            >
              {portalLoading ? "Opening…" : "Change Plan"}
            </button>
          ) : (
            <Link
              to="/pricing"
              className="px-4 py-2 rounded-xl bg-primary text-on-primary text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Upgrade
            </Link>
          )}
        </div>
        {portalError && <p className="mt-3 text-sm text-error">{portalError}</p>}
      </div>

      {(addonsLoading || addons.length > 0) && (
        <div className="rounded-2xl bg-surface-container-lowest shadow-cloud ring-1 ring-outline-variant/20 p-6 mb-8">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-sm font-semibold text-on-surface-variant uppercase tracking-wider">
              Your Add-ons
            </h2>
            <Link
              to="/pricing"
              className="text-xs font-semibold text-primary hover:opacity-80 transition-opacity"
            >
              Browse add-ons
            </Link>
          </div>
          {addonsLoading ? (
            <p className="text-sm text-on-surface-variant">Loading add-ons…</p>
          ) : (
            <div className="divide-y divide-surface-container-high">
              {addons.map((addon) => {
                const label = ADDON_LABELS[addon.addon_key] || addon.addon_key;
                const price = formatPrice(addon.amount);
                return (
                  <div
                    key={addon.addon_key}
                    className="py-3 flex items-start justify-between gap-4"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-on-surface">
                        {label}
                        {addon.quantity > 1 ? (
                          <span className="text-on-surface-variant"> × {addon.quantity}</span>
                        ) : null}
                      </div>
                      <div className="text-xs text-on-surface-variant mt-0.5">
                        {addonTimingLabel(addon)}
                      </div>
                    </div>
                    {price && (
                      <span className="text-sm text-on-surface whitespace-nowrap shrink-0">
                        {price}
                        {addon.cadence === "monthly" ? "/mo" : ""}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl bg-surface-container-lowest shadow-cloud ring-1 ring-outline-variant/20 p-6">
        <h2 className="text-sm font-semibold text-on-surface-variant uppercase tracking-wider mb-4">
          Usage This Month
        </h2>
        {loading && !usage ? (
          <p className="text-sm text-on-surface-variant">Loading usage…</p>
        ) : (
          <div className="divide-y divide-surface-container-high">
            {FEATURE_ORDER.map((feature) => (
              <div key={feature} className="py-3 flex items-center justify-between gap-4">
                <span className="text-sm text-on-surface">{FEATURE_LABELS[feature]}</span>
                <UsageMeter item={usage?.[feature]} className="justify-end text-right" />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Subscription;
